/** Lead reads: instant from IndexedDB, fresh from Supabase REST — with a
 * LYING-READ guard (an "OK" response that would shrink a known-large dataset
 * by half is treated as a failed read, never truth).
 *
 * KEYSET pagination (2026-07-16): offset paging made Postgres re-sort the
 * whole table for every page — measurable pain on a resource-starved
 * instance. Each page now anchors on the previous page's last
 * (created_at, id), which uses the index directly, and is immune to the
 * skip/duplicate drift offset paging suffers while scrapers insert rows
 * mid-fetch. Pages are fetched SEQUENTIALLY on purpose: v2 must be a polite
 * tenant of the same database v1 runs the business on. */
import { http } from "../../lib/http";
import { moneyExact } from "../../lib/money";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import { narrowLead, type Lead } from "../../types";
import { backendFetch } from "../../lib/backend";
import { pageAll } from "../edits/api";

const CACHE_KEY = "leads_v1";
const PAGE = 500;
const MAX_PAGES = 80; // hard ceiling: 40k rows — beyond that something is wrong
const COLS = "id,created_at,address,source,price,arv,county,beds,baths,sqft,raw";

/** TOMBSTONES (2026-07-16, the "381 missing leads" that weren't): a deleted
 * lead's row KEEPS existing with raw.dismissed=true so scrapers can't
 * resurrect it — that's v1's design and it is correct. A dismissed row must
 * never render. Filtered server-side (saves shipping the fat raw column for
 * 380+ dead rows) AND guarded client-side, because a business rule this
 * important must not depend on a URL staying right. */
const NOT_DISMISSED = "or(raw->>dismissed.is.null,raw->>dismissed.neq.true)";

/** v1's source list — index.html `const SOURCES=[...]`, verbatim. The filter
 * offers these PLUS whatever actually appears in the data (superset, so a
 * legacy/renamed source can never hide rows). */
export const V1_SOURCES = [
  "Facebook - VA", "Direct To Seller", "Email", "SMS", "Manual", "MLS",
  "Facebook", "Wholesaler", "Mark's Leads", "Mark's Coming Soon", "Investorlift", "Zillow", "Driving for Dollars",
  "Direct Mail", "Referral", "Agent referral", "Cold Call", "Other",
];

export const isTombstone = (row: unknown): boolean => {
  const raw = (row as { raw?: { dismissed?: unknown } } | null)?.raw;
  return raw?.dismissed === true || raw?.dismissed === "true";
};

export interface LeadsResult {
  leads: Lead[];
  fromCache: boolean;
  dropped: number; // malformed rows refused at the door
  tombstoned: number; // deleted leads correctly hidden (normal, not an error)
}

export interface Keyset {
  ts: string; // raw created_at exactly as PostgREST returned it
  id: string;
}

/** ONE combined PostgREST filter param: tombstones out, and (for pages
 * after the first) only rows strictly after the cursor in
 * (created_at desc, id desc) order. A single and=() because two top-level
 * or= params don't compose reliably in PostgREST. Values are URL-encoded
 * (timestamps carry '+', which query strings would otherwise eat as a
 * space). */
export function pageFilter(cursor: Keyset | null): string {
  if (!cursor) return `&and=(${NOT_DISMISSED})`;
  const ts = encodeURIComponent(cursor.ts);
  const id = encodeURIComponent(cursor.id);
  return `&and=(${NOT_DISMISSED},or(created_at.lt.${ts},and(created_at.eq.${ts},id.lt.${id})))`;
}

export async function cachedLeads(): Promise<Lead[] | null> {
  const c = await idbGet<{ at: number; leads: Lead[] }>(CACHE_KEY);
  if (!c || !Array.isArray(c.leads)) return null;
  if (Date.now() - c.at > 7 * 86400000) return null; // week-stale: refuse
  return c.leads;
}

export async function fetchLeads(token: string, expectedAtLeast: number, signal?: AbortSignal): Promise<LeadsResult> {
  const headers = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
  };
  const rows: unknown[] = [];
  let cursor: Keyset | null = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const batch = await http<unknown[]>(
      `${SUPABASE_URL}/rest/v1/properties?select=${COLS}&order=created_at.desc,id.desc&limit=${PAGE}${pageFilter(cursor)}`,
      { headers, signal, timeoutMs: 20000 },
    );
    if (!Array.isArray(batch) || batch.length === 0) break;
    rows.push(...batch);
    if (batch.length < PAGE) break;
    const last = batch[batch.length - 1] as { created_at?: unknown; id?: unknown };
    if (typeof last.created_at !== "string" || typeof last.id !== "string") break; // cursor unreadable: stop rather than loop
    cursor = { ts: last.created_at, id: last.id };
  }
  let dropped = 0;
  let tombstoned = 0;
  const leads: Lead[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    if (isTombstone(r)) {
      tombstoned++; // deleted lead — hidden by design, server filter or not
      continue;
    }
    const l = narrowLead(r as Record<string, unknown>);
    if (!l) {
      dropped++;
      continue;
    }
    if (seen.has(l.id)) continue;
    seen.add(l.id);
    leads.push(l);
  }
  // LYING-READ GUARD (v1 lesson: a stale-token RLS read returns 200 + empty
  // and once wiped the list): if we KNOW there should be more, refuse it.
  if (expectedAtLeast > 8 && leads.length < expectedAtLeast / 2) {
    throw new Error(`lying-read: got ${leads.length}, expected ≥${Math.round(expectedAtLeast / 2)}`);
  }
  await idbSet(CACHE_KEY, { at: Date.now(), leads });
  return { leads, fromCache: false, dropped, tombstoned };
}

// ---- LIVE NEW-LEAD PICKUP (2026-09-14) --------------------------------------
// Tom: "Why it doesnt create leads in the system from the number that sent
// deals now" — it did, within a second of each text (lead row + "New lead
// from a text" notification). His TAB couldn't show it: the lead list was
// read ONCE at boot, so a deal texted in an hour later was a lead on the
// server and nothing on his screen until the next reload. This reads only
// the rows newer than what the tab already holds — one small keyset page,
// never the full 3.5k — cheap enough to run every minute while visible.

/** Newest created_at (epoch ms) the tab holds; 0 for an empty list. */
export function newestLeadMs(leads: Lead[]): number {
  let m = 0;
  for (const l of leads) if (l.createdAt > m) m = l.createdAt;
  return m;
}

/** Rows created after `newestKnownMs` (minus a 5-minute margin, so a row
 * committed late under an earlier timestamp still shows up; duplicates are
 * dropped by id in mergeNewLeads). Tombstones filtered exactly like the
 * boot read. Throws on a bad read — the caller just tries again next tick. */
export async function fetchNewLeads(token: string, newestKnownMs: number, signal?: AbortSignal): Promise<Lead[]> {
  const since = new Date(Math.max(0, newestKnownMs - 5 * 60_000)).toISOString();
  const rows = await http<unknown[]>(
    `${SUPABASE_URL}/rest/v1/properties?select=${COLS}&order=created_at.desc,id.desc&limit=300` +
      `&and=(${NOT_DISMISSED},created_at.gte.${encodeURIComponent(since)})`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }, signal, timeoutMs: 15000, attempts: 1 },
  );
  if (!Array.isArray(rows)) return [];
  const out: Lead[] = [];
  for (const r of rows) {
    if (isTombstone(r)) continue;
    const l = narrowLead(r as Record<string, unknown>);
    if (l) out.push(l);
  }
  return out;
}

/** Prepend what the tab doesn't have yet. Rows it already holds keep the copy
 * on screen (never clobbered by a re-read). Returns the SAME array when
 * nothing is new, so React skips the re-render. */
export function mergeNewLeads(existing: Lead[], fresh: Lead[]): Lead[] {
  const have = new Set(existing.map((l) => l.id));
  const add = fresh.filter((l) => !have.has(l.id));
  if (add.length === 0) return existing;
  return [...add, ...existing];
}

// ---- Phase 8: batch delete (v1 batchDelete parity) --------------------------

/** v1 tombstone key: lowercased trimmed address (LEAD_TOMBS entries). */
export const leadTombKey = (address: string): string => String(address ?? "").toLowerCase().trim();

/** {s:'tombs', op:'add', k:'lead', d:[keys]} — the sync-side half of delete:
 * every device (v1 and v2) hides tombstoned addresses even if a row lingers. */
export function opTombsAddLeads(addresses: string[]): import("../../lib/outbox").RecOp {
  return { s: "tombs", op: "add", k: "lead", d: [...new Set(addresses.map(leadTombKey).filter(Boolean))] };
}

/** The scraper-proof half: atomically stamps raw.dismissed on the properties
 * row (server-side merge — unlike v1's whole-blob PATCH, this can never
 * clobber a concurrent price-monitor write). Returns true if the row was
 * ours and got stamped. */
export async function dismissProperty(token: string, id: string): Promise<boolean> {
  const r = await http<boolean>(`${SUPABASE_URL}/rest/v1/rpc/crm2_dismiss_property`, {
    method: "POST",
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
    body: { p_id: id },
    attempts: 1,
    timeoutMs: 15000,
  });
  return r === true;
}

// ============================================================================
// MANUAL LEADS  —  the "Add Property" side of v1 (PHASE 16, 2026-07-18)
//
// v1's leads list is TWO populations merged into one array (`DATA`): scraped
// rows from `properties` (handled above) AND manual / SMS-parsed / wholesaler
// leads from `rec_leads` (v1's blob `DATA`, keyed by lowercased address). v2
// only read `properties` — so hand-added and inbox leads were invisible here.
// This reads `rec_leads` too and narrows each row into the SAME Lead shape,
// so the page merges them exactly like v1 (both shown, dup badge across them).
//
// The Add Property FORM writes one `leads` put through crm2_apply_ops (the
// PHASE 16 door section); the row's shape is v1's `submitAddProperty` verbatim
// so v1 renders it identically. The Create button stays Phase-B-gated (a real
// row in the shared DB, same rule as Send/Upload) — the door + selftest prove
// the path now; the button flips on in the sit-down.
// ============================================================================

const MANUAL_CACHE_KEY = "manual_leads_v1";
const MANUAL_COLS = "nat_key,address,source,data,created_at,updated_at";

/** money/number parse — mirrors types.ts `num` ("$270,000" → 270000). */
const mNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const f = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(f) ? f : null;
};
const mStr = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
const okCty = (v: unknown): string | null => {
  const s = mStr(v);
  return s && s !== "—" ? s : null;
};

/** v1 `_recNorm` / `_kLead`: the rec_leads natural key is the lowercased,
 * trimmed address. The Add Property door enforces k = lower(trim(d.a)). */
export const manualKey = (address: string): string => String(address ?? "").toLowerCase().trim();

/** Narrow a rec_leads row (v1 `DATA` shape) into the list's Lead type. Manual
 * leads carry only what was typed (+ any recheckMarket enrichment); the
 * scraped-only arrays default empty, exactly as v1 renders them. Status, notes
 * and ARV overrides live in the shared edits map (rec_lead_fields), keyed by
 * address — so they light up for manual leads with no work here. */
export function narrowManualLead(row: unknown): Lead | null {
  if (row === null || typeof row !== "object" || Array.isArray(row)) return null;
  const r = row as Record<string, unknown>;
  const data = (r["data"] && typeof r["data"] === "object" && !Array.isArray(r["data"]) ? r["data"] : {}) as Record<string, unknown>;
  const edits = (data["edits"] && typeof data["edits"] === "object" && !Array.isArray(data["edits"]) ? data["edits"] : {}) as Record<string, unknown>;
  const address = mStr(r["address"]) ?? mStr(data["a"]);
  if (!address) return null;
  const natRaw = mStr(r["nat_key"]) ?? manualKey(address);
  const price = mNum(data["price"]);
  const arv = mNum(data["arv"]);
  let arvPct = mNum(data["aarv"]);
  if (arvPct === null && price !== null && arv !== null && arv > 0) arvPct = Math.round((price / arv) * 100);
  else if (arvPct !== null) arvPct = Math.round(arvPct);
  const createdAt =
    mNum(data["_ts"]) ??
    (typeof r["created_at"] === "string" ? Date.parse(r["created_at"]) || 0 : 0);
  // source: rec_leads column, else v1's badge array `b` (first known source), else Manual
  let source = mStr(r["source"]) ?? mStr(data["source"]);
  if (!source && Array.isArray(data["b"])) {
    source = (data["b"] as unknown[]).map(mStr).find((x): x is string => !!x && V1_SOURCES.includes(x)) ?? null;
  }
  const market = mStr(data["marketStatus"]);
  const marketStatus: "on" | "off" | null =
    market === "on" ? "on" : market === "off" ? "off" : data["onMarket"] === true ? "on" : null;
  return {
    id: "m:" + natRaw, // never collides with a properties uuid
    createdAt,
    address,
    source: source ?? "Manual",
    price,
    arv,
    arvPct,
    county: okCty(data["cty"]) ?? okCty(edits["county"]),
    beds: mNum(data["beds"]),
    baths: mNum(data["baths"]),
    sqft: mNum(data["sqft"]),
    zest: mNum(data["zest"]),
    rentZest: mNum(data["rentZest"]),
    cdom: null,
    seller: mStr(edits["owner"]),
    marketStatus,
    distressed: data["distressed"] === true,
    jobTag: typeof data["jobTag"] === "string" ? (data["jobTag"] as string).trim() : "",
    priceDrop: null, // genuine in-CRM drops are a scraped-monitor signal
    reductions: 0,
    priceDrops: [],
    priceHistory: [],
    photos: [],
    desc: mStr(edits["notes"]),
    taxHistory: [],
    yearBuilt: mNum(edits["yearBuilt"]) ?? mNum(data["year"]),
    permits: Array.isArray(data["permits"]) ? data["permits"] : null,
    permitsChecked: data["permitsChecked"] === true,
    permitUrl: mStr(edits["permitUrl"]),
  };
}

/** Read the org's manual leads (rec_leads), paged until a short page —
 * PostgREST clamps any single-request limit to 1000, so "one big limit" is a
 * silent truncation, not a cap (that bug hid 2,540 contacts on 2026-07-23).
 * Deleted (deleted_at) rows are filtered server-side. Failures return [] so
 * the scraped list still renders (manual is additive, never a gate). */
export async function fetchManualLeads(token: string, signal?: AbortSignal): Promise<Lead[]> {
  try {
    // paged: PostgREST clamps limit to 1000 — a single shot silently truncates
    const rows = await pageAll<unknown>(token, `rec_leads?select=${MANUAL_COLS}&deleted_at=is.null&order=updated_at.desc`, signal);
    const out: Lead[] = [];
    const seen = new Set<string>();
    for (const r of Array.isArray(rows) ? rows : []) {
      const l = narrowManualLead(r);
      if (!l || seen.has(l.id)) continue;
      seen.add(l.id);
      out.push(l);
    }
    await idbSet(MANUAL_CACHE_KEY, { at: Date.now(), leads: out });
    return out;
  } catch {
    const c = await idbGet<{ at: number; leads: Lead[] }>(MANUAL_CACHE_KEY);
    return c && Array.isArray(c.leads) ? c.leads : [];
  }
}

// ---- Add Property form → v1 lead row → door op ------------------------------

/** v1 index.html property-type list (Add Property `ptype` select). */
export const PROPERTY_TYPES = ["Single Family", "Townhouse", "Condo", "Duplex", "Multi-Family", "Land"];

export interface AddPropertyForm {
  address: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  ptype: string;
  beds: string;
  baths: string;
  sqft: string;
  year: string;
  lot: string;
  price: string;
  arv: string;
  rehab: string;
  status: string;
  source: string;
  notes: string;
  pool: boolean;
  garage: boolean;
  sinkhole: boolean;
  foundation: boolean;
  distressed: boolean;
  onMarket: boolean;
  underContract: boolean;
}

export const emptyAddPropertyForm = (): AddPropertyForm => ({
  address: "", city: "", county: "", state: "", zip: "", ptype: "", beds: "", baths: "",
  sqft: "", year: "", lot: "", price: "", arv: "", rehab: "", status: "", source: "",
  notes: "", pool: false, garage: false, sinkhole: false, foundation: false,
  distressed: false, onMarket: false, underContract: false,
});

/** "$270,000" — v1 `_fmtUSD` (idempotent; leaves "—"/blank alone). */
export const fmtUSD = (v: string): string => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || n === 0) return s;
  return moneyExact(n);
};

/** v1 `submitAddProperty`'s full-address rule: don't re-append city/state when
 * the address already looks complete (has a comma, e.g. from autocomplete). */
export const fullAddress = (f: AddPropertyForm): string => {
  const a = f.address.trim();
  if (!a) return "";
  if (a.includes(",")) return a;
  return a + (f.city.trim() ? ", " + f.city.trim() : "") + (f.state.trim() ? ", " + f.state.trim() : "");
};

/** v1 lead pipeline stages that live in the row's `stage`/`qual` (not badges). */
const LEAD_STAGES = ["Pre-Check"];

/** Build the rec_leads `data` row, v1 `submitAddProperty` verbatim, so v1
 * renders the manual lead identically. Pure (nowMs injected for tests). */
export function buildManualLeadRow(
  f: AddPropertyForm,
  opts: { nowMs: number; zillowUrl?: string | null } = { nowMs: 0 },
): Record<string, unknown> {
  const a = fullAddress(f);
  const st = f.status.trim();
  const b: string[] = [];
  if (f.source.trim()) b.push(f.source.trim());
  if (st && !LEAD_STAGES.includes(st)) b.push(st);
  b.push("New");
  const price = fmtUSD(f.price) || "$0";
  const arv = f.arv.trim() ? fmtUSD(f.arv) : "—";
  const d = new Date(opts.nowMs);
  const row: Record<string, unknown> = {
    a,
    b,
    price,
    cty: f.county.trim() || "—",
    beds: mNum(f.beds) ?? 0,
    baths: mNum(f.baths) ?? 0,
    sqft: f.sqft.trim() || "—",
    arv,
    rehab: f.rehab.trim() || "—",
    roi: "—",
    distressed: f.distressed,
    marketStatus: f.onMarket ? "on" : "off",
    onMarket: f.onMarket,
    _ts: opts.nowMs,
    dt: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
  };
  // ZIP was collected by the Add Property form and then dropped on the floor —
  // it appeared in no column and no edit, so you typed it and it vanished.
  // Scraped leads carry it, so the drawer already knows how to show one.
  if (f.zip.trim()) row["zip"] = f.zip.trim();
  if (f.city.trim()) row["city"] = f.city.trim();
  if (f.state.trim()) row["state"] = f.state.trim().toUpperCase();
  if (st && LEAD_STAGES.includes(st)) { row["stage"] = st; row["qual"] = st; }
  if (f.source.trim()) row["source"] = f.source.trim();
  const ak = mNum(price), av = mNum(arv);
  if (ak && av) row["aarv"] = Math.round((ak / av) * 100) + "%";
  // edits: the fields v1 tucks under `edits` so the detail view shows them
  const edits: Record<string, unknown> = {};
  if (f.ptype.trim()) { row["homeType"] = f.ptype.trim(); edits["ptype"] = f.ptype.trim(); }
  if (f.year.trim()) edits["yearBuilt"] = f.year.trim();
  if (f.lot.trim()) edits["lotSize"] = f.lot.trim();
  if (f.notes.trim()) edits["notes"] = f.notes.trim();
  if (f.pool) edits["pool"] = "Yes";
  if (f.garage) edits["garage"] = "Yes";
  if (f.sinkhole) edits["sinkhole"] = "Yes";
  if (f.foundation) edits["foundationIssues"] = "Yes";
  if (f.underContract) edits["underContract"] = "Yes";
  if (opts.zillowUrl) edits["zillowUrl"] = opts.zillowUrl;
  if (Object.keys(edits).length) row["edits"] = edits;
  return row;
}

/** {s:'leads', op:'put', k:lower(trim(a)), d:row} — the PHASE 16 door section.
 * The core writes address/stage/qual/source columns + the whole row as data. */
export function opAddLead(row: Record<string, unknown>): import("../../lib/outbox").RecOp {
  const a = typeof row["a"] === "string" ? row["a"] : "";
  return { s: "leads", op: "put", k: manualKey(a), d: row };
}

/** {s:'leads', op:'del', k:lower(trim(a))} — soft-deletes the rec_leads row so
 * a deleted manual lead can't refetch (and v1 sees the deletion too). Manual
 * leads carry an "m:"-prefixed id; scraped rows keep going through
 * dismissProperty (the properties table). */
export const isManualLead = (id: string): boolean => id.startsWith("m:");
export function opDelLead(address: string): import("../../lib/outbox").RecOp {
  return { s: "leads", op: "del", k: manualKey(address) };
}

// ---- read-only backend verify (Phase-A safe: no writes, no sends) -----------

export interface AddrMatch { address: string }

/** v1 attachAddrAutocomplete → GET /api/arv/normalize?q= (free US Census
 * geocoder via the backend). Read-only; safe in Phase A. */
export async function normalizeAddress(
  token: string, q: string, opts: { onWake?: () => void; signal?: AbortSignal } = {},
): Promise<AddrMatch[]> {
  try {
    const j = await backendFetch<{ matches?: unknown }>(
      token, "/api/arv/normalize?q=" + encodeURIComponent(q),
      { timeoutMs: 15000, ...(opts.onWake ? { onWake: opts.onWake } : {}), ...(opts.signal ? { signal: opts.signal } : {}) },
    );
    const ms = Array.isArray(j?.matches) ? j.matches : [];
    return ms.map((m) => ({ address: mStr((m as Record<string, unknown>)?.["address"]) ?? "" })).filter((m) => m.address);
  } catch {
    return [];
  }
}

export interface ZillowVerify { found: boolean; data: Record<string, unknown> | null; noApi: boolean }

/** v1 zillowCheck → POST /api/inbox/market-check. Reads Zillow/county to
 * confirm the address exists and pull enrichment; fires nothing on the user's
 * behalf, so it runs in Phase A. noApi = backend has no scraper key set. */
export async function zillowVerify(
  token: string, address: string, opts: { onWake?: () => void; signal?: AbortSignal } = {},
): Promise<ZillowVerify> {
  try {
    const j = await backendFetch<{ data?: unknown }>(
      token, "/api/inbox/market-check",
      { method: "POST", body: { address, id: null }, timeoutMs: 45000, ...(opts.onWake ? { onWake: opts.onWake } : {}), ...(opts.signal ? { signal: opts.signal } : {}) },
    );
    const data = (j?.data && typeof j.data === "object" && !Array.isArray(j.data) ? j.data : {}) as Record<string, unknown>;
    const found = !!(data["property_url"] || data["zestimate"] || data["zpid"]);
    return { found, data, noApi: false };
  } catch (e) {
    // a real 4xx/5xx means the check ran and failed; only treat a total miss
    // as "no api". Either way the form still lets the user add the property.
    return { found: false, data: null, noApi: true };
  }
}

/** Phase-B gate for the Add Property CREATE. GO-LIVE 2026-07-19: flipped ON in
 * the supervised sit-down (the leads door + selftest proved the write; reads and
 * the address check already worked in Phase A). */
export const ADD_PROPERTY_LIVE = true;

export const ADD_MSG = {
  title: "Add Property",
  addressRequired: "Enter a property address first.",
  checking: "Checking the address on Zillow…",
  verified: "Verified on Zillow ✓",
  notFound: "Not found on Zillow — you can still add it (county, flood & permits still work).",
  unavailable: "Address check unavailable right now — you can still add it.",
  createPhaseB: "Saving new properties goes live in the Phase B sit-down — the form and address check work now.",
};
